import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  TextField,
  MenuItem,
  InputAdornment,
  Button,
  IconButton,
  Tooltip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Drawer,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Pagination,
  Stack,
  Typography,
} from '@mui/material';
import {
  Search,
  FilterList,
  Add,
  Refresh,
  Groups,
  PersonAdd,
  Cake,
  TrendingDown,
  Visibility,
  VisibilityOff,
  Close,
} from '@mui/icons-material';
import {
  PageHeader,
  StatCard,
  FilterBar,
  FilterSpacer,
  TableSurface,
  headerCellSx,
  bodyRowSx,
  StatusDot,
  StatusTone,
  TableEmptyState,
} from '../../components/common/pageKit';
import { formatCurrency } from '../../utils/currency';
import {
  hrService,
  Department,
  Designation,
  Employee,
  EmployeeListItem,
  EmployeePagination,
  EmployeePayload,
  EmployeeStatus,
  EmploymentType,
  HrSummary,
} from '../../services/hrService';

const statusTone: Record<EmployeeStatus, StatusTone> = {
  ACTIVE: 'success',
  ON_LEAVE: 'info',
  NOTICE: 'warning',
  EXITED: 'neutral',
  SUSPENDED: 'error',
};

const statusLabel: Record<EmployeeStatus, string> = {
  ACTIVE: 'Active',
  ON_LEAVE: 'On Leave',
  NOTICE: 'Notice Period',
  EXITED: 'Exited',
  SUSPENDED: 'Suspended',
};

const employmentTypeLabel: Record<EmploymentType, string> = {
  FULL_TIME: 'Full-time',
  PART_TIME: 'Part-time',
  CONTRACT: 'Contract',
  INTERN: 'Intern',
};

const toNumber = (v: number | string | null | undefined): number | null => {
  if (v === null || v === undefined) return null;
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return Number.isFinite(n) ? n : null;
};

const money = (v: number | string | null | undefined): string => {
  const n = toNumber(v);
  return n === null ? '—' : formatCurrency(n);
};

const initials = (first: string, last: string | null): string =>
  `${first?.[0] ?? ''}${last?.[0] ?? ''}`.toUpperCase() || '?';

const fullName = (e: { first_name: string; last_name: string | null }): string =>
  [e.first_name, e.last_name].filter(Boolean).join(' ');

/** Masks all but the last 4 characters. Used for bank account numbers. */
const maskLast4 = (value: string): string => {
  const tail = value.slice(-4);
  return `${'•'.repeat(Math.max(value.length - 4, 4))}${tail}`;
};

/** Masks a PAN (ABCDE1234F) to its first two and last two characters. */
const maskPan = (pan: string): string => {
  if (pan.length <= 4) return '•'.repeat(pan.length);
  return `${pan.slice(0, 2)}${'•'.repeat(pan.length - 4)}${pan.slice(-2)}`;
};

const emptyForm: EmployeePayload = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  dateOfBirth: '',
  dateOfJoining: '',
  departmentId: undefined,
  designationId: undefined,
  managerId: undefined,
  employmentType: 'FULL_TIME',
  workLocation: '',
  ctcAnnual: undefined,
  pan: '',
  aadhaarLast4: '',
  bankAccount: '',
  bankIfsc: '',
  pfNumber: '',
  uan: '',
  esiNumber: '',
};

/** Masked-by-default reveal for a single sensitive field in the detail drawer. */
const RevealField: React.FC<{ label: string; value: string | null; mask: (v: string) => string }> = ({
  label,
  value,
  mask,
}) => {
  const [revealed, setRevealed] = useState(false);
  if (!value) {
    return (
      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {label}
        </Typography>
        <Typography variant="body2">—</Typography>
      </Box>
    );
  }
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
          {revealed ? value : mask(value)}
        </Typography>
        <IconButton size="small" onClick={() => setRevealed((r) => !r)} aria-label={revealed ? `Hide ${label}` : `Reveal ${label}`}>
          {revealed ? <VisibilityOff sx={{ fontSize: 16 }} /> : <Visibility sx={{ fontSize: 16 }} />}
        </IconButton>
      </Box>
    </Box>
  );
};

const EmployeesPage: React.FC = () => {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState<EmployeeListItem[]>([]);
  const [pagination, setPagination] = useState<EmployeePagination | null>(null);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [designations, setDesignations] = useState<Designation[]>([]);
  const [managerOptions, setManagerOptions] = useState<EmployeeListItem[]>([]);
  const [summary, setSummary] = useState<HrSummary | null>(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [statusFilter, setStatusFilter] = useState<EmployeeStatus | ''>('');
  const [departmentFilter, setDepartmentFilter] = useState<number | ''>('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const [detailId, setDetailId] = useState<number | null>(null);
  const [detailEmployee, setDetailEmployee] = useState<Employee | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [statusUpdating, setStatusUpdating] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<EmployeePayload>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Debounce the free-text search so every keystroke doesn't trigger a request.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    Promise.all([hrService.getDepartments(), hrService.getDesignations(), hrService.getEmployees({ limit: 100 })])
      .then(([deptRes, desigRes, empRes]) => {
        setDepartments(deptRes.departments);
        setDesignations(desigRes.designations);
        setManagerOptions(empRes.employees);
      })
      .catch(() => {
        /* lookups are supplementary — the main list/error below still surfaces failures */
      });
    hrService.getSummary().then(setSummary).catch(() => undefined);
  }, []);

  const fetchEmployees = useCallback(() => {
    setLoading(true);
    setError(null);
    hrService
      .getEmployees({
        status: statusFilter || undefined,
        departmentId: departmentFilter === '' ? undefined : departmentFilter,
        search: search || undefined,
        page,
        limit: 20,
      })
      .then((res) => {
        setEmployees(res.employees);
        setPagination(res.pagination);
      })
      .catch((err: any) => setError(err.message || 'Failed to load employees'))
      .finally(() => setLoading(false));
  }, [statusFilter, departmentFilter, search, page]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  const openDetail = (id: number) => {
    setDetailId(id);
    setDetailEmployee(null);
    setDetailError(null);
    setDetailLoading(true);
    hrService
      .getEmployeeById(id)
      .then((res) => setDetailEmployee(res.employee))
      .catch((err: any) => setDetailError(err.message || 'Failed to load employee'))
      .finally(() => setDetailLoading(false));
  };

  const closeDetail = () => {
    setDetailId(null);
    setDetailEmployee(null);
    setDetailError(null);
  };

  const openCreateForm = () => {
    setEditingId(null);
    setForm(emptyForm);
    setSaveError(null);
    setFormOpen(true);
  };

  const openEditForm = (e: Employee) => {
    setEditingId(e.id);
    setForm({
      firstName: e.first_name,
      lastName: e.last_name || '',
      email: e.email || '',
      phone: e.phone || '',
      dateOfBirth: e.date_of_birth ? e.date_of_birth.slice(0, 10) : '',
      dateOfJoining: e.date_of_joining.slice(0, 10),
      departmentId: e.department_id ?? undefined,
      designationId: e.designation_id ?? undefined,
      managerId: e.manager_id ?? undefined,
      employmentType: e.employment_type,
      workLocation: e.work_location || '',
      ctcAnnual: toNumber(e.ctc_annual) ?? undefined,
      pan: e.pan || '',
      aadhaarLast4: e.aadhaar_last4 || '',
      bankAccount: e.bank_account || '',
      bankIfsc: e.bank_ifsc || '',
      pfNumber: e.pf_number || '',
      uan: e.uan || '',
      esiNumber: e.esi_number || '',
    });
    setSaveError(null);
    setFormOpen(true);
  };

  const closeForm = () => {
    if (saving) return;
    setFormOpen(false);
  };

  const handleSave = async () => {
    if (!form.firstName.trim() || !form.dateOfJoining) {
      setSaveError('First name and date of joining are required');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const payload: EmployeePayload = {
        ...form,
        lastName: form.lastName || undefined,
        email: form.email || undefined,
        phone: form.phone || undefined,
        dateOfBirth: form.dateOfBirth || undefined,
        workLocation: form.workLocation || undefined,
        pan: form.pan || undefined,
        aadhaarLast4: form.aadhaarLast4 || undefined,
        bankAccount: form.bankAccount || undefined,
        bankIfsc: form.bankIfsc || undefined,
        pfNumber: form.pfNumber || undefined,
        uan: form.uan || undefined,
        esiNumber: form.esiNumber || undefined,
      };
      if (editingId) {
        await hrService.updateEmployee(editingId, payload);
        if (detailId === editingId) openDetail(editingId);
      } else {
        await hrService.createEmployee(payload);
      }
      setFormOpen(false);
      fetchEmployees();
    } catch (err: any) {
      setSaveError(err.message || 'Failed to save employee');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (id: number, status: EmployeeStatus) => {
    setStatusUpdating(true);
    try {
      const dateOfLeaving = status === 'EXITED' ? new Date().toISOString().slice(0, 10) : undefined;
      const updated = await hrService.updateEmployeeStatus(id, status, dateOfLeaving);
      setDetailEmployee(updated);
      fetchEmployees();
    } catch (err: any) {
      setDetailError(err.message || 'Failed to update status');
    } finally {
      setStatusUpdating(false);
    }
  };

  return (
    <Box>
      <PageHeader
        title="Employees"
        subtitle="Directory, department & designation assignment, employment status"
        actions={
          <>
            <Tooltip title="Refresh">
              <IconButton onClick={fetchEmployees} disabled={loading}>
                <Refresh />
              </IconButton>
            </Tooltip>
            <Button variant="contained" startIcon={<Add />} size="small" onClick={openCreateForm}>
              New Employee
            </Button>
          </>
        }
      />

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard title="Headcount" value={summary?.headcount ?? null} icon={<Groups />} color="#0284c7" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard title="Joining This Month" value={summary?.joiningThisMonth ?? null} icon={<PersonAdd />} color="#0d9488" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard title="Birthdays This Week" value={summary?.birthdaysThisWeek ?? null} icon={<Cake />} color="#db2777" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Attrition Rate (12mo)"
            value={summary?.attritionRate === null || summary?.attritionRate === undefined ? null : `${summary.attritionRate}%`}
            icon={<TrendingDown />}
            color="#e11d48"
          />
        </Grid>
      </Grid>

      <FilterBar>
        <TextField
          size="small"
          placeholder="Search name, code or email"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          sx={{ minWidth: 260, flexGrow: { xs: 1, md: 0 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ fontSize: 18, color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          select
          size="small"
          label="Status"
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value as EmployeeStatus | '');
            setPage(1);
          }}
          sx={{ minWidth: 180 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FilterList sx={{ fontSize: 18, color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
        >
          <MenuItem value="">All Status</MenuItem>
          {(Object.keys(statusLabel) as EmployeeStatus[]).map((s) => (
            <MenuItem key={s} value={s}>
              {statusLabel[s]}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Department"
          value={departmentFilter}
          onChange={(e) => {
            setDepartmentFilter(e.target.value === '' ? '' : Number(e.target.value));
            setPage(1);
          }}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">All Departments</MenuItem>
          {departments.map((d) => (
            <MenuItem key={d.id} value={d.id}>
              {d.dept_name}
            </MenuItem>
          ))}
        </TextField>
        <FilterSpacer />
        <Typography variant="body2" color="text.secondary">
          {pagination ? <strong>{pagination.total}</strong> : null} employees
        </Typography>
      </FilterBar>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <TableSurface>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow>
                {['Employee', 'Department', 'Designation', 'Type', 'Status', 'Joined', 'CTC (Annual)'].map((h, i) => (
                  <TableCell key={h} align={i === 6 ? 'right' : 'left'} sx={headerCellSx}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6, border: 0 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : employees.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ border: 0 }}>
                    <TableEmptyState
                      title="No employees match these filters"
                      hint="Clear the search, status or department filter to see the full directory."
                    />
                  </TableCell>
                </TableRow>
              ) : (
                employees.map((emp) => (
                  <TableRow key={emp.id} sx={{ ...bodyRowSx, cursor: 'pointer' }} onClick={() => openDetail(emp.id)}>
                    <TableCell sx={{ py: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                        <Avatar sx={{ width: 34, height: 34, fontSize: '0.8125rem', bgcolor: 'primary.main' }}>
                          {initials(emp.first_name, emp.last_name)}
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                            {fullName(emp)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                            {emp.employee_code}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{emp.dept_name || '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{emp.designation_title || '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{employmentTypeLabel[emp.employment_type]}</Typography>
                    </TableCell>
                    <TableCell>
                      <StatusDot label={statusLabel[emp.status]} tone={statusTone[emp.status]} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{new Date(emp.date_of_joining).toLocaleDateString('en-IN')}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {money(emp.ctc_annual)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TableSurface>

      {pagination && pagination.totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination count={pagination.totalPages} page={page} onChange={(_, p) => setPage(p)} color="primary" />
        </Box>
      )}

      {/* Detail drawer */}
      <Drawer anchor="right" open={detailId !== null} onClose={closeDetail}>
        <Box sx={{ width: { xs: '100vw', sm: 420 }, p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Employee Details
            </Typography>
            <IconButton size="small" onClick={closeDetail}>
              <Close />
            </IconButton>
          </Box>

          {detailLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          )}

          {detailError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {detailError}
            </Alert>
          )}

          {detailEmployee && !detailLoading && (
            <Stack spacing={2.5}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ width: 56, height: 56, fontSize: '1.25rem', bgcolor: 'primary.main' }}>
                  {initials(detailEmployee.first_name, detailEmployee.last_name)}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }} noWrap>
                    {fullName(detailEmployee)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                    {detailEmployee.employee_code}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <StatusDot label={statusLabel[detailEmployee.status]} tone={statusTone[detailEmployee.status]} />
                <TextField
                  select
                  size="small"
                  label="Change status"
                  value=""
                  onChange={(e) => handleStatusChange(detailEmployee.id, e.target.value as EmployeeStatus)}
                  disabled={statusUpdating}
                  sx={{ minWidth: 170, ml: 'auto' }}
                >
                  {(Object.keys(statusLabel) as EmployeeStatus[])
                    .filter((s) => s !== detailEmployee.status)
                    .map((s) => (
                      <MenuItem key={s} value={s}>
                        {statusLabel[s]}
                      </MenuItem>
                    ))}
                </TextField>
              </Box>

              <Divider />

              <Box>
                <Typography variant="overline" color="text.secondary">
                  Employment
                </Typography>
                <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                  <Grid size={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Department
                    </Typography>
                    <Typography variant="body2">{detailEmployee.dept_name || '—'}</Typography>
                  </Grid>
                  <Grid size={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Designation
                    </Typography>
                    <Typography variant="body2">{detailEmployee.designation_title || '—'}</Typography>
                  </Grid>
                  <Grid size={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Manager
                    </Typography>
                    <Typography variant="body2">{detailEmployee.manager_name || '—'}</Typography>
                  </Grid>
                  <Grid size={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Type
                    </Typography>
                    <Typography variant="body2">{employmentTypeLabel[detailEmployee.employment_type]}</Typography>
                  </Grid>
                  <Grid size={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Joined
                    </Typography>
                    <Typography variant="body2">{new Date(detailEmployee.date_of_joining).toLocaleDateString('en-IN')}</Typography>
                  </Grid>
                  <Grid size={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Left
                    </Typography>
                    <Typography variant="body2">
                      {detailEmployee.date_of_leaving ? new Date(detailEmployee.date_of_leaving).toLocaleDateString('en-IN') : '—'}
                    </Typography>
                  </Grid>
                  <Grid size={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Work Location
                    </Typography>
                    <Typography variant="body2">{detailEmployee.work_location || '—'}</Typography>
                  </Grid>
                  <Grid size={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      CTC (Annual)
                    </Typography>
                    <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {money(detailEmployee.ctc_annual)}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              <Divider />

              <Box>
                <Typography variant="overline" color="text.secondary">
                  Contact
                </Typography>
                <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                  <Grid size={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Email
                    </Typography>
                    <Typography variant="body2" noWrap>
                      {detailEmployee.email || '—'}
                    </Typography>
                  </Grid>
                  <Grid size={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Phone
                    </Typography>
                    <Typography variant="body2">{detailEmployee.phone || '—'}</Typography>
                  </Grid>
                  <Grid size={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Date of Birth
                    </Typography>
                    <Typography variant="body2">
                      {detailEmployee.date_of_birth ? new Date(detailEmployee.date_of_birth).toLocaleDateString('en-IN') : '—'}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              <Divider />

              <Box>
                <Typography variant="overline" color="text.secondary">
                  Statutory &amp; Banking
                </Typography>
                <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mb: 1 }}>
                  Masked by default — click the eye icon to reveal.
                </Typography>
                <Grid container spacing={1.5}>
                  <Grid size={6}>
                    <RevealField label="PAN" value={detailEmployee.pan} mask={maskPan} />
                  </Grid>
                  <Grid size={6}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        Aadhaar
                      </Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {detailEmployee.aadhaar_last4 ? `•••• •••• ${detailEmployee.aadhaar_last4}` : '—'}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid size={6}>
                    <RevealField label="Bank Account" value={detailEmployee.bank_account} mask={maskLast4} />
                  </Grid>
                  <Grid size={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      IFSC
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {detailEmployee.bank_ifsc || '—'}
                    </Typography>
                  </Grid>
                  <Grid size={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      PF Number
                    </Typography>
                    <Typography variant="body2">{detailEmployee.pf_number || '—'}</Typography>
                  </Grid>
                  <Grid size={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      UAN
                    </Typography>
                    <Typography variant="body2">{detailEmployee.uan || '—'}</Typography>
                  </Grid>
                  <Grid size={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      ESI Number
                    </Typography>
                    <Typography variant="body2">{detailEmployee.esi_number || '—'}</Typography>
                  </Grid>
                </Grid>
              </Box>

              <Stack direction="row" spacing={1}>
                <Button variant="contained" onClick={() => navigate(`/hr/employees/${detailEmployee.id}/360`)}>
                  Open Full Profile
                </Button>
                <Button variant="outlined" onClick={() => openEditForm(detailEmployee)}>
                  Edit Employee
                </Button>
              </Stack>
            </Stack>
          )}
        </Box>
      </Drawer>

      {/* Create / edit dialog */}
      <Dialog open={formOpen} onClose={closeForm} maxWidth="md" fullWidth>
        <DialogTitle>{editingId ? 'Edit Employee' : 'New Employee'}</DialogTitle>
        <DialogContent dividers>
          {saveError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {saveError}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 0.25 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="First Name"
                required
                value={form.firstName}
                onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="Last Name"
                value={form.lastName}
                onChange={(e) => setForm((f) => ({ ...f, lastName: e.target.value }))}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="Email"
                type="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="Phone"
                value={form.phone}
                onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="Date of Birth"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={form.dateOfBirth}
                onChange={(e) => setForm((f) => ({ ...f, dateOfBirth: e.target.value }))}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="Date of Joining"
                type="date"
                required
                InputLabelProps={{ shrink: true }}
                value={form.dateOfJoining}
                onChange={(e) => setForm((f) => ({ ...f, dateOfJoining: e.target.value }))}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                select
                fullWidth
                size="small"
                label="Department"
                value={form.departmentId ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, departmentId: e.target.value === '' ? undefined : Number(e.target.value) }))}
              >
                <MenuItem value="">—</MenuItem>
                {departments.map((d) => (
                  <MenuItem key={d.id} value={d.id}>
                    {d.dept_name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                select
                fullWidth
                size="small"
                label="Designation"
                value={form.designationId ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, designationId: e.target.value === '' ? undefined : Number(e.target.value) }))}
              >
                <MenuItem value="">—</MenuItem>
                {designations.map((d) => (
                  <MenuItem key={d.id} value={d.id}>
                    {d.title}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                select
                fullWidth
                size="small"
                label="Manager"
                value={form.managerId ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, managerId: e.target.value === '' ? undefined : Number(e.target.value) }))}
              >
                <MenuItem value="">—</MenuItem>
                {managerOptions
                  .filter((m) => m.id !== editingId)
                  .map((m) => (
                    <MenuItem key={m.id} value={m.id}>
                      {fullName(m)} ({m.employee_code})
                    </MenuItem>
                  ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                select
                fullWidth
                size="small"
                label="Employment Type"
                value={form.employmentType}
                onChange={(e) => setForm((f) => ({ ...f, employmentType: e.target.value as EmploymentType }))}
              >
                {(Object.keys(employmentTypeLabel) as EmploymentType[]).map((t) => (
                  <MenuItem key={t} value={t}>
                    {employmentTypeLabel[t]}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="Work Location"
                value={form.workLocation}
                onChange={(e) => setForm((f) => ({ ...f, workLocation: e.target.value }))}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="CTC (Annual)"
                type="number"
                value={form.ctcAnnual ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, ctcAnnual: e.target.value === '' ? undefined : Number(e.target.value) }))}
                InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
              />
            </Grid>
            <Grid size={12}>
              <Divider>
                <Typography variant="caption" color="text.secondary">
                  Statutory &amp; Banking (optional)
                </Typography>
              </Divider>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                size="small"
                label="PAN"
                value={form.pan}
                onChange={(e) => setForm((f) => ({ ...f, pan: e.target.value.toUpperCase() }))}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                size="small"
                label="Aadhaar (last 4 digits)"
                value={form.aadhaarLast4}
                inputProps={{ maxLength: 4 }}
                onChange={(e) => setForm((f) => ({ ...f, aadhaarLast4: e.target.value.replace(/\D/g, '').slice(0, 4) }))}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                size="small"
                label="Bank IFSC"
                value={form.bankIfsc}
                onChange={(e) => setForm((f) => ({ ...f, bankIfsc: e.target.value.toUpperCase() }))}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                size="small"
                label="Bank Account Number"
                value={form.bankAccount}
                onChange={(e) => setForm((f) => ({ ...f, bankAccount: e.target.value }))}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField fullWidth size="small" label="PF Number" value={form.pfNumber} onChange={(e) => setForm((f) => ({ ...f, pfNumber: e.target.value }))} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField fullWidth size="small" label="UAN" value={form.uan} onChange={(e) => setForm((f) => ({ ...f, uan: e.target.value }))} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeForm} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : editingId ? 'Save Changes' : 'Create Employee'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmployeesPage;
